import { type FunctionComponent, useState } from 'react';
import { useParams } from 'react-router';

import { Input, ThemeProvider } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import { AdminPage, UserProfileHeader } from 'components/admin';
import RemoveAdminModal from 'components/admin/RemoveAdminModal';

import { useAdminUsers } from 'hooks/useAdminUsers';
import Error404Page from 'pages/Error404';

import { changeAdminStatus } from 'services/user';

import { MDASH, getAdminStatus, useGlobalStateObject } from 'utils';

import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import S from './style.scss';

type StatusOptionT = 'Active' | 'Delete Admin User' | 'Inactive';

const AdminAdminProfilePage: FunctionComponent = () => {
  const { id } = useParams();
  if (!id) throw Error('Missing admin user ID');

  const adminUsers = useAdminUsers();
  const adminUser = adminUsers.data?.find((item) => item.id === id);

  const gs = useGlobalStateObject();

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  if (!adminUser && !adminUsers.loading) return <Error404Page />;

  let subTitle = 'Admin Users';
  if (adminUser) subTitle += ` / ${adminUser.firstName} ${adminUser.lastName}`;

  const status = adminUser ? getAdminStatus(adminUser) : '';

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle={subTitle}
      title="Admin Profile"
    >
      {adminUser ? (
        <>
          <UserProfileHeader
            setStatus={(value) => {
              const newStatus = value as StatusOptionT;
              switch (newStatus) {
                case 'Active':
                case 'Inactive':
                  void (async () => {
                    try {
                      const user = await changeAdminStatus(id, newStatus);

                      let au = gs.get('adminUsers.data');
                      if (au) {
                        const idx = au.findIndex((item) => item.id === id);
                        if (idx >= 0) {
                          au = [...au];
                          au[idx] = user;
                          gs.set('adminUsers.data', au);
                        }
                      }
                    } catch (error) {
                      captureException(error);
                      // TODO: Better error message.
                      // eslint-disable-next-line no-alert
                      alert('Failed to change admin status');
                    }
                  })();
                  break;
                case 'Delete Admin User':
                  setShowRemoveModal(true);
                  break;
                default: throw Error('Unexpected status');
              }
            }}
            status={status}
            statusOptions={[
              'Active',
              'Inactive',
              'Delete Admin User',
            ] satisfies StatusOptionT[]}
            user={adminUser}
          />
          {showRemoveModal ? (
            <RemoveAdminModal
              onCancel={() => {
                setShowRemoveModal(false);
              }}
              userId={id}
            />
          ) : null}
          <div styleName="mainContent">
            <ThemeProvider themes={{ Input: inputTheme }}>
              <Input
                disabled
                label="First Name"
                value={adminUser.firstName}
              />
              <Input
                disabled
                label="Last Name"
                value={adminUser.lastName}
              />
              <Input
                disabled
                label="Email Address"
                value={adminUser.email ?? MDASH}
              />
              <Input
                disabled
                label="Mobile Number"
                value={adminUser.phone ?? MDASH}
              />
            </ThemeProvider>
          </div>
        </>
      ) : null}
      {adminUsers.loading ? <Throbber withOverlay /> : null}
    </AdminPage>
  );
};

export default AdminAdminProfilePage;
